/**
 * Macro #26 · Image → Voxel converter V1 — PURE (Increment 2).
 *
 * Approved SIMPLE V1 ONLY (msg §22):
 *   IMAGE → pixel/color grid → voxel grid → SIMPLE UNIFORM EXTRUSION.
 * NO AI 3D reconstruction, NO photogrammetry, NO brightness-depth inference,
 * NO smooth mesh, NO GLB import. The converter helps the creator START; manual
 * voxel editing remains available afterward.
 *
 * Auto-fit preserves proportions, respects the real XY footprint bounds, the
 * z ≤ 30 height law, and the available capacity (voxels are dropped from the far
 * face of the extrusion, never silently over-capacity).
 */
import { Z_MAX_FREE, type Voxel, type WorkArt } from "./voxel3d";

export interface ColorGrid {
  width: number;
  height: number;
  /** row-major; null = transparent / skipped pixel. */
  cells: (string | null)[];
}

/** Nearest-neighbour downscale of a color grid into a target w×h (preserves the
 * dominant color per target cell; transparent if the sampled source is null). */
export function downscaleGrid(grid: ColorGrid, targetW: number, targetH: number): ColorGrid {
  const tw = Math.max(1, Math.floor(targetW));
  const th = Math.max(1, Math.floor(targetH));
  const cells: (string | null)[] = new Array(tw * th).fill(null);
  for (let ty = 0; ty < th; ty++) {
    for (let tx = 0; tx < tw; tx++) {
      const sx = Math.min(grid.width - 1, Math.floor((tx + 0.5) * (grid.width / tw)));
      const sy = Math.min(grid.height - 1, Math.floor((ty + 0.5) * (grid.height / th)));
      cells[ty * tw + tx] = grid.cells[sy * grid.width + sx] ?? null;
    }
  }
  return { width: tw, height: th, cells };
}

/** Proportional fit of (w,h) into (maxW,maxH) preserving aspect ratio. */
export function proportionalFit(w: number, h: number, maxW: number, maxH: number): { w: number; h: number } {
  if (w <= 0 || h <= 0) return { w: 0, h: 0 };
  const s = Math.min(maxW / w, maxH / h, 1);
  return { w: Math.max(1, Math.floor(w * s)), h: Math.max(1, Math.floor(h * s)) };
}

export interface ConvertOptions {
  /** requested extrusion depth in voxels (clamped to [1, Z_MAX_FREE]). */
  depth: number;
  /** max XY footprint the result must fit within (owned-territory bounds). */
  maxW: number;
  maxH: number;
  /** available valid voxel capacity (result is trimmed to never exceed it). */
  capacity: number;
  /** placement origin on the ground. */
  placement?: { x: number; y: number; z: number };
  workArtId: string;
}

export interface ConvertResult {
  workArt: WorkArt;
  used: number;
  truncated: boolean; // true if capacity forced fewer voxels than the full extrusion
}

/**
 * Convert a color grid to a single Work Art via uniform extrusion.
 * The image lies on the XY plane (image row → world Y), extruded along Z.
 */
export function imageToWorkArt(grid: ColorGrid, opts: ConvertOptions): ConvertResult {
  const depth = Math.max(1, Math.min(Z_MAX_FREE, Math.floor(opts.depth)));
  const fit = proportionalFit(grid.width, grid.height, Math.max(1, Math.floor(opts.maxW)), Math.max(1, Math.floor(opts.maxH)));
  const small = downscaleGrid(grid, fit.w, fit.h);

  // Collect opaque columns first (each is `depth` voxels), then fill layer by
  // layer so that a capacity cap trims from the FAR face uniformly rather than
  // dropping whole columns (keeps the silhouette intact).
  const columns: Array<{ x: number; y: number; color: string }> = [];
  for (let y = 0; y < small.height; y++) {
    for (let x = 0; x < small.width; x++) {
      const c = small.cells[y * small.width + x];
      if (c) columns.push({ x, y, color: c });
    }
  }
  const capacity = Math.max(0, Math.floor(opts.capacity));
  const fullTotal = columns.length * depth;
  const voxels: Voxel[] = [];
  let used = 0;
  outer: for (let z = 0; z < depth; z++) {
    for (const col of columns) {
      if (used >= capacity) break outer;
      voxels.push([col.x, col.y, z, col.color]);
      used += 1;
    }
  }
  const placement = opts.placement ?? { x: 0, y: 0, z: 0 };
  const workArt: WorkArt = {
    work_art_id: opts.workArtId,
    allowed_volume: { w: small.width, h: small.height, d: depth },
    placement,
    rotation: 0,
    voxels,
  };
  return { workArt, used, truncated: used < fullTotal };
}

/**
 * Decode an ImageData-like buffer (RGBA row-major) into a ColorGrid, treating
 * pixels below `alphaThreshold` as transparent and quantizing to `#rrggbb`.
 */
export function imageDataToColorGrid(
  data: Uint8ClampedArray | number[],
  width: number,
  height: number,
  alphaThreshold = 16,
): ColorGrid {
  const cells: (string | null)[] = new Array(width * height).fill(null);
  for (let i = 0; i < width * height; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    const a = data[i * 4 + 3];
    if (a === undefined || a < alphaThreshold) {
      cells[i] = null;
      continue;
    }
    cells[i] = "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, v | 0)).toString(16).padStart(2, "0")).join("");
  }
  return { width, height, cells };
}
